import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Info, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ApiError, getErrorCodeForUi } from '@/api/errors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useSetBreadcrumbs } from '@/components/shell/ShellContext';
import { toast } from '@/hooks/useToast';

import { useTenantRoles, useTenants } from '@/features/platform/api';

import {
  credentialMatchesPlane,
  useAuthProvider,
  useAuthProviderMutations,
  useOidcCredentials,
  type AuthProviderInput,
  type AuthProviderViewModel,
} from '../api';
import type { UnmappedRolePolicyDto } from '../endpoints';
import { LoginCaptureSection } from './LoginCaptureSection';
import { TypeToConfirmDialog } from './TypeToConfirmDialog';

interface RoleMappingDraft {
  externalRole: string;
  internalRole: string;
}

interface Draft {
  host: string;
  tenantUuid: string;
  credentialId: string;
  tenantIdClaimKey: string;
  externalTenantId: string;
  tenantClaimCheckEnabled: boolean;
  userIdClaimKey: string;
  rolesClaimKey: string;
  rolesFromClaimEnabled: boolean;
  unmappedRolePolicy: UnmappedRolePolicyDto;
  userinfoEnabled: boolean;
  claimName: string;
  claimFirstName: string;
  claimLastName: string;
  claimEmail: string;
  jitProvisioningEnabled: boolean;
  newUserAccountsAreActive: boolean;
  enabled: boolean;
  roleMappings: RoleMappingDraft[];
  defaultRoles: string[];
}

/**
 * The three "footgun" transitions gated behind {@link TypeToConfirmDialog} (PTV2-1412): enabling JIT
 * provisioning, activating new accounts immediately, and assigning any role (a default role or a
 * claim→role mapping). Toggling any of these OFF, or removing an already-assigned role, is the safe
 * direction and needs no gate.
 */
type RiskyAction = 'jit' | 'active' | 'role';
type RoleAddKind = 'mapping' | 'default';

const RISKY_ACTION_WORDS: Record<RiskyAction, string> = {
  jit: 'ENABLE',
  active: 'ACTIVATE',
  role: 'ASSIGN',
};

const UNMAPPED_POLICIES: readonly UnmappedRolePolicyDto[] = ['FALLBACK_TO_DEFAULTS', 'DROP', 'TRUST_CLAIM'];

function draftFrom(row: AuthProviderViewModel): Draft {
  return {
    host: row.host,
    tenantUuid: row.tenantUuid,
    credentialId: row.credentialId,
    tenantIdClaimKey: row.tenantIdClaimKey,
    externalTenantId: row.externalTenantId,
    tenantClaimCheckEnabled: row.tenantClaimCheckEnabled,
    userIdClaimKey: row.userIdClaimKey,
    rolesClaimKey: row.rolesClaimKey,
    rolesFromClaimEnabled: row.rolesFromClaimEnabled,
    unmappedRolePolicy: row.unmappedRolePolicy,
    userinfoEnabled: row.userinfoEnabled,
    claimName: row.claimName,
    claimFirstName: row.claimFirstName,
    claimLastName: row.claimLastName,
    claimEmail: row.claimEmail,
    jitProvisioningEnabled: row.jitProvisioningEnabled,
    newUserAccountsAreActive: row.newUserAccountsAreActive,
    enabled: row.enabled,
    roleMappings: row.roleMappings.map((m) => ({ ...m })),
    defaultRoles: [...row.defaultRoles],
  };
}

interface FieldErrors {
  host?: string;
  credentialId?: string;
  userIdClaimKey?: string;
}

/**
 * Full auth-provider record page (PTV2-1383). Replaces the old monolithic edit modal: the identity
 * fields are collected in a small create dialog, and everything else is configured here across
 * organised sections. Ports the field JSX, the risky-action type-to-confirm gates and the tenant
 * role-catalogue pickers from the retired `AuthProviderFormDialog`.
 */
export function AuthProviderDetailView(): ReactElement {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const fp = 'routes.admin.authProviders.form';
  const dp = 'routes.admin.authProviders.detail';

  const providerQuery = useAuthProvider(id);
  const credentialsQuery = useOidcCredentials();
  const { update } = useAuthProviderMutations();

  const provider = providerQuery.data ?? null;
  const credentials = useMemo(() => credentialsQuery.data ?? [], [credentialsQuery.data]);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [riskyAction, setRiskyAction] = useState<RiskyAction | null>(null);
  const [roleAddKind, setRoleAddKind] = useState<RoleAddKind | null>(null);

  // Seed the editable draft once the provider loads. Keyed on id so navigating between providers
  // re-seeds; a background refetch of the same id does not clobber an in-progress edit.
  useEffect(() => {
    if (provider !== null) {
      setDraft(draftFrom(provider));
      setErrors({});
      setRiskyAction(null);
      setRoleAddKind(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, provider !== null]);

  useSetBreadcrumbs([
    { label: t('shell.topNav.admin'), to: '/platform' },
    { label: t('routes.platform.sidebar.authProviders'), to: '/platform/auth-providers' },
    ...(provider === null ? [] : [{ label: provider.host }]),
  ]);

  const tenantUuid = (draft?.tenantUuid ?? '').trim();
  const tenantRolesQuery = useTenantRoles(tenantUuid);
  const tenantsQuery = useTenants();
  const tenants = useMemo(() => tenantsQuery.data?.tenants ?? [], [tenantsQuery.data]);
  const tenantRoleNames = useMemo(
    () => (tenantRolesQuery.data ?? []).map((r) => r.name),
    [tenantRolesQuery.data],
  );
  const hasRoleCatalog = tenantUuid.length > 0 && tenantRolesQuery.isSuccess;

  // Loading / error / not-found frames.
  if (draft === null) {
    return (
      <div className="flex h-full flex-col">
        <header className="border-b border-border bg-card px-8 py-3">
          {providerQuery.isPending && <Skeleton className="h-7 w-64" />}
          {providerQuery.isError &&
            (providerQuery.error instanceof ApiError && providerQuery.error.status === 404 ? (
              <p role="alert" className="text-sm text-red-600">
                {t(`${dp}.notFound`)}
              </p>
            ) : (
              <div className="max-w-xl">
                <FetchErrorCallout
                  error={providerQuery.error}
                  heading={t(`${dp}.error.heading`)}
                  message={t(`${dp}.error.message`)}
                  codeLabel={t(`${dp}.error.codeLabel`)}
                  onRetry={() => {
                    providerQuery.refetch().catch(() => undefined);
                  }}
                  retryLabel={t(`${dp}.error.retry`)}
                />
              </div>
            ))}
        </header>
      </div>
    );
  }

  function set<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((p) => (p === null ? p : { ...p, [key]: value }));
  }

  /** Applies whichever risky transition is currently gated, then closes the confirm dialog. */
  function confirmRiskyAction(): void {
    if (riskyAction === 'jit') {
      set('jitProvisioningEnabled', true);
    } else if (riskyAction === 'active') {
      set('newUserAccountsAreActive', true);
    } else if (riskyAction === 'role') {
      if (roleAddKind === 'mapping') {
        setDraft((p) =>
          p === null
            ? p
            : { ...p, roleMappings: [...p.roleMappings, { externalRole: '', internalRole: '' }] },
        );
      } else if (roleAddKind === 'default') {
        setDraft((p) => (p === null ? p : { ...p, defaultRoles: [...p.defaultRoles, ''] }));
      }
    }
    setRiskyAction(null);
    setRoleAddKind(null);
  }

  function cancelRiskyAction(): void {
    setRiskyAction(null);
    setRoleAddKind(null);
  }

  // PTV2-1383: the provider inherits its login type from the bound credential — the inherited type is
  // shown read-only. PTV2-1520: the plane (Global vs a tenant) is fixed after creation, and the bindable
  // credentials are filtered to that plane (the currently-bound one is always kept so a save never drops
  // a grandfathered mismatch).
  const isGlobalPlane = draft.tenantUuid.trim().length === 0;
  const selectedCredential = credentials.find((c) => c.id === draft.credentialId);
  const inheritedType = selectedCredential?.type ?? provider?.type ?? null;
  const visibleCredentials = credentials.filter(
    (c) => credentialMatchesPlane(c.type, isGlobalPlane) || c.id === draft.credentialId,
  );
  // Resolve the fixed plane to a display label; an unknown stored UUID (deleted tenant) is preserved.
  const boundTenant = tenants.find((tn) => tn.uuid === draft.tenantUuid.trim());
  const planeLabel = isGlobalPlane
    ? t(`${fp}.fields.planeGlobal`)
    : boundTenant !== undefined
      ? `${boundTenant.label.length > 0 ? boundTenant.label : boundTenant.name} (${boundTenant.name})`
      : t(`${fp}.fields.planeUnknown`, { uuid: draft.tenantUuid.trim() });

  /** Catalogue names plus the current value (if not already present) so a save never silently drops an existing assignment. */
  function roleOptionsFor(currentValue: string): readonly string[] {
    const trimmed = currentValue.trim();
    return trimmed.length > 0 && !tenantRoleNames.includes(trimmed)
      ? [...tenantRoleNames, trimmed]
      : tenantRoleNames;
  }

  function updateRoleMapping(index: number, patch: Partial<RoleMappingDraft>): void {
    setDraft((p) =>
      p === null
        ? p
        : { ...p, roleMappings: p.roleMappings.map((m, i) => (i === index ? { ...m, ...patch } : m)) },
    );
  }

  function updateDefaultRole(index: number, value: string): void {
    setDraft((p) =>
      p === null
        ? p
        : { ...p, defaultRoles: p.defaultRoles.map((r, i) => (i === index ? value : r)) },
    );
  }

  const requiredComplete =
    draft.host.trim().length > 0 &&
    draft.credentialId.trim().length > 0 &&
    draft.userIdClaimKey.trim().length > 0;

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (draft === null) return;
    const next: FieldErrors = {};
    if (draft.host.trim().length === 0) next.host = t(`${fp}.errors.hostRequired`);
    if (draft.credentialId.trim().length === 0)
      next.credentialId = t(`${fp}.errors.credentialRequired`);
    if (draft.userIdClaimKey.trim().length === 0)
      next.userIdClaimKey = t(`${fp}.errors.userIdClaimRequired`);
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    const input: AuthProviderInput = {
      host: draft.host,
      tenantUuid: draft.tenantUuid,
      credentialId: draft.credentialId,
      tenantIdClaimKey: draft.tenantIdClaimKey,
      // PTV2-1520: external tenant id + tenant-claim toggle apply only to a tenant plane. On Global they
      // are inert (the fields are disabled), but send the stored values as-is — the backend ignores them.
      externalTenantId: draft.externalTenantId,
      tenantClaimCheckEnabled: draft.tenantClaimCheckEnabled,
      userIdClaimKey: draft.userIdClaimKey,
      rolesClaimKey: draft.rolesClaimKey,
      rolesFromClaimEnabled: draft.rolesFromClaimEnabled,
      unmappedRolePolicy: draft.unmappedRolePolicy,
      userinfoEnabled: draft.userinfoEnabled,
      claimName: draft.claimName,
      claimFirstName: draft.claimFirstName,
      claimLastName: draft.claimLastName,
      claimEmail: draft.claimEmail,
      jitProvisioningEnabled: draft.jitProvisioningEnabled,
      newUserAccountsAreActive: draft.newUserAccountsAreActive,
      enabled: draft.enabled,
      roleMappings: draft.roleMappings,
      defaultRoles: draft.defaultRoles,
    };
    update.mutate(
      { ...input, id },
      {
        onSuccess: () => {
          update.reset();
          toast({ title: t(`${dp}.saved`), variant: 'success' });
        },
      },
    );
  }

  const confirmWord = riskyAction !== null ? RISKY_ACTION_WORDS[riskyAction] : '';
  const enabledToggleDisabled = !draft.enabled && !requiredComplete;

  return (
    <div className="flex h-full flex-col">
      <form onSubmit={handleSubmit} noValidate className="flex h-full flex-col">
        {/* Identity band + primary action. */}
        <header className="border-b border-border bg-card px-8 py-3">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="flex items-center gap-2 text-[16px] font-semibold tracking-tight">
                <ShieldCheck className="h-[18px] w-[18px] text-muted-foreground" aria-hidden="true" />
                <span className="truncate font-mono">{draft.host}</span>
                <Badge variant={draft.enabled ? 'default' : 'outline'}>
                  {draft.enabled
                    ? t('routes.admin.authProviders.table.enabledBadge')
                    : t('routes.admin.authProviders.table.disabledBadge')}
                </Badge>
              </h1>
              <p className="mt-1 text-[12px] text-muted-foreground">
                {inheritedType !== null
                  ? t(`routes.admin.authProviders.typeLabel.${inheritedType}`)
                  : t(`${dp}.typeInheritedUnknown`)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" size="sm" loading={update.isPending}>
                {t(`${dp}.save`)}
              </Button>
            </div>
          </div>
        </header>

        {/* Scroll region. */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-background">
          <div className="mx-auto max-w-[900px] space-y-6 px-8 py-6">
            {/* Section 1 — General */}
            <section aria-labelledby="section-general" className="space-y-4 rounded-md border border-border bg-card p-5">
              <h2 id="section-general" className="text-sm font-semibold text-foreground">
                {t(`${dp}.sections.general`)}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  id="provider-host"
                  label={t(`${fp}.fields.host`)}
                  hint={t(`${fp}.hints.host`)}
                  required
                  error={errors.host}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={draft.host}
                      onChange={(e) => {
                        set('host', e.target.value);
                      }}
                      placeholder="nygh.phenotips-staging.com"
                      autoComplete="off"
                      className="font-mono text-sm"
                    />
                  )}
                </FormField>
                <FormField
                  id="provider-type"
                  label={t(`${fp}.fields.type`)}
                  hint={t(`${fp}.hints.typeInherited`)}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={
                        inheritedType !== null
                          ? t(`routes.admin.authProviders.typeLabel.${inheritedType}`)
                          : ''
                      }
                      readOnly
                      disabled
                      className="text-sm"
                    />
                  )}
                </FormField>
                <FormField
                  id="provider-credential"
                  label={t(`${fp}.fields.credential`)}
                  hint={t(`${fp}.hints.credentialForPlane`)}
                  required
                  error={errors.credentialId}
                >
                  {(f) => (
                    <Select
                      {...f}
                      value={draft.credentialId}
                      onChange={(e) => {
                        set('credentialId', e.target.value);
                      }}
                    >
                      <option value="">{t(`${fp}.fields.credentialPlaceholder`)}</option>
                      {visibleCredentials.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label} · {t(`routes.admin.oidcCredentials.typeLabel.${c.type}`)}
                        </option>
                      ))}
                    </Select>
                  )}
                </FormField>
                <FormField
                  id="provider-plane"
                  label={t(`${fp}.fields.plane`)}
                  hint={t(`${fp}.hints.planeFixed`)}
                >
                  {(f) => (
                    <Input {...f} value={planeLabel} readOnly disabled className="text-sm" />
                  )}
                </FormField>
              </div>

              {/* Toggles: JIT, activate, enabled. */}
              <div className="space-y-4 border-t border-border pt-4">
                <div className="space-y-1">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.jitProvisioningEnabled}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRiskyAction('jit');
                        } else {
                          set('jitProvisioningEnabled', false);
                        }
                      }}
                      className="h-3.5 w-3.5"
                    />
                    {t(`${fp}.fields.jitProvisioningEnabled`)}
                  </label>
                  <p className="flex items-start gap-1.5 pl-6 text-xs text-muted-foreground">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span>{t(`${dp}.info.jit.${draft.jitProvisioningEnabled ? 'on' : 'off'}`)}</span>
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.newUserAccountsAreActive}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRiskyAction('active');
                        } else {
                          set('newUserAccountsAreActive', false);
                        }
                      }}
                      className="h-3.5 w-3.5"
                    />
                    {t(`${fp}.fields.newUserAccountsAreActive`)}
                  </label>
                  <p className="flex items-start gap-1.5 pl-6 text-xs text-muted-foreground">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span>
                      {t(`${dp}.info.active.${draft.newUserAccountsAreActive ? 'on' : 'off'}`)}
                    </span>
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.enabled}
                      disabled={enabledToggleDisabled}
                      onChange={(e) => {
                        set('enabled', e.target.checked);
                      }}
                      className="h-3.5 w-3.5"
                    />
                    {t(`${fp}.fields.enabled`)}
                  </label>
                  {enabledToggleDisabled && (
                    <p className="pl-6 text-xs text-muted-foreground" role="note">
                      {t(`${dp}.enabledHint`)}
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* Section 2 — Claim mapping */}
            <section aria-labelledby="section-claims" className="space-y-4 rounded-md border border-border bg-card p-5">
              <h2 id="section-claims" className="text-sm font-semibold text-foreground">
                {t(`${fp}.sections.claims`)}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField
                  id="provider-user-id-claim"
                  label={t(`${fp}.fields.userIdClaimKey`)}
                  hint={t(`${fp}.hints.userIdClaimKey`)}
                  required
                  error={errors.userIdClaimKey}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={draft.userIdClaimKey}
                      onChange={(e) => {
                        set('userIdClaimKey', e.target.value);
                      }}
                      autoComplete="off"
                      className="font-mono text-sm"
                    />
                  )}
                </FormField>
                <FormField
                  id="provider-tenant-id-claim"
                  label={t(`${fp}.fields.tenantIdClaimKey`)}
                  hint={t(`${fp}.hints.tenantIdClaimKey`)}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={draft.tenantIdClaimKey}
                      onChange={(e) => {
                        set('tenantIdClaimKey', e.target.value);
                      }}
                      autoComplete="off"
                      className="font-mono text-sm"
                    />
                  )}
                </FormField>
                <FormField
                  id="provider-roles-claim"
                  label={t(`${fp}.fields.rolesClaimKey`)}
                  hint={t(`${fp}.hints.rolesClaimKey`)}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={draft.rolesClaimKey}
                      onChange={(e) => {
                        set('rolesClaimKey', e.target.value);
                      }}
                      autoComplete="off"
                      className="font-mono text-sm"
                    />
                  )}
                </FormField>
              </div>

              {/* PTV2-1520: external tenant id + tenant-claim check — apply to the tenant-assertion path
                  only, so both are disabled (greyed, with a hover note) when the plane is Global. */}
              <div className="grid grid-cols-1 gap-4 border-t border-border pt-4 sm:grid-cols-2">
                <FormField
                  id="provider-external-tenant-id"
                  label={t(`${fp}.fields.externalTenantId`)}
                  hint={isGlobalPlane ? t(`${fp}.hints.notUsedWhenGlobal`) : t(`${fp}.hints.externalTenantId`)}
                >
                  {(f) => (
                    <Input
                      {...f}
                      value={draft.externalTenantId}
                      onChange={(e) => {
                        set('externalTenantId', e.target.value);
                      }}
                      disabled={isGlobalPlane}
                      title={isGlobalPlane ? t(`${fp}.hints.notUsedWhenGlobal`) : undefined}
                      maxLength={255}
                      placeholder="org_ab12cd34"
                      autoComplete="off"
                      className="font-mono text-sm"
                    />
                  )}
                </FormField>
              </div>
              <div className="space-y-1">
                <label
                  className="inline-flex items-center gap-2 text-sm"
                  title={isGlobalPlane ? t(`${fp}.hints.notUsedWhenGlobal`) : undefined}
                >
                  <input
                    type="checkbox"
                    checked={draft.tenantClaimCheckEnabled}
                    disabled={isGlobalPlane}
                    onChange={(e) => {
                      set('tenantClaimCheckEnabled', e.target.checked);
                    }}
                    className="h-3.5 w-3.5"
                  />
                  {t(`${fp}.fields.tenantClaimCheckEnabled`)}
                </label>
                <p className="flex items-start gap-1.5 pl-6 text-xs text-muted-foreground">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span>{t(`${fp}.hints.tenantClaimCheckEnabled`)}</span>
                </p>
                {!isGlobalPlane && !draft.tenantClaimCheckEnabled && (
                  <p role="alert" className="pl-6 text-xs text-amber-600">
                    {t(`${fp}.hints.tenantClaimDisabledWarning`)}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-6">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.rolesFromClaimEnabled}
                    onChange={(e) => {
                      set('rolesFromClaimEnabled', e.target.checked);
                    }}
                    className="h-3.5 w-3.5"
                  />
                  {t(`${fp}.fields.rolesFromClaimEnabled`)}
                </label>
                <FormField
                  id="provider-unmapped-policy"
                  label={t(`${fp}.fields.unmappedRolePolicy`)}
                  className="min-w-[16rem]"
                >
                  {(f) => (
                    <Select
                      {...f}
                      value={draft.unmappedRolePolicy}
                      onChange={(e) => {
                        set('unmappedRolePolicy', e.target.value as UnmappedRolePolicyDto);
                      }}
                    >
                      {UNMAPPED_POLICIES.map((policy) => (
                        <option key={policy} value={policy}>
                          {t(`routes.admin.authProviders.policyLabel.${policy}`)}
                        </option>
                      ))}
                    </Select>
                  )}
                </FormField>
              </div>
            </section>

            {/* Section 3 — User info */}
            <section aria-labelledby="section-userinfo" className="space-y-4 rounded-md border border-border bg-card p-5">
              <h2 id="section-userinfo" className="text-sm font-semibold text-foreground">
                {t(`${dp}.sections.userinfo`)}
              </h2>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.userinfoEnabled}
                  onChange={(e) => {
                    set('userinfoEnabled', e.target.checked);
                  }}
                  className="h-3.5 w-3.5"
                />
                {t(`${fp}.fields.userinfoEnabled`)}
              </label>
              <FormField
                id="provider-claim-name"
                label={t(`${fp}.fields.claimName`)}
                hint={t(`${fp}.hints.claimName`)}
              >
                {(f) => (
                  <Input
                    {...f}
                    value={draft.claimName}
                    onChange={(e) => {
                      set('claimName', e.target.value);
                    }}
                    placeholder="name"
                    autoComplete="off"
                    className="font-mono text-sm"
                  />
                )}
              </FormField>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <FormField id="provider-claim-first" label={t(`${fp}.fields.claimFirstName`)}>
                  {(f) => (
                    <Input
                      {...f}
                      value={draft.claimFirstName}
                      onChange={(e) => {
                        set('claimFirstName', e.target.value);
                      }}
                      autoComplete="off"
                      className="font-mono text-sm"
                    />
                  )}
                </FormField>
                <FormField id="provider-claim-last" label={t(`${fp}.fields.claimLastName`)}>
                  {(f) => (
                    <Input
                      {...f}
                      value={draft.claimLastName}
                      onChange={(e) => {
                        set('claimLastName', e.target.value);
                      }}
                      autoComplete="off"
                      className="font-mono text-sm"
                    />
                  )}
                </FormField>
                <FormField id="provider-claim-email" label={t(`${fp}.fields.claimEmail`)}>
                  {(f) => (
                    <Input
                      {...f}
                      value={draft.claimEmail}
                      onChange={(e) => {
                        set('claimEmail', e.target.value);
                      }}
                      autoComplete="off"
                      className="font-mono text-sm"
                    />
                  )}
                </FormField>
              </div>
            </section>

            {/* Section 4 — Roles (mappings + defaults) */}
            <section aria-labelledby="section-roles" className="space-y-6 rounded-md border border-border bg-card p-5">
              <h2 id="section-roles" className="text-sm font-semibold text-foreground">
                {t(`${dp}.sections.roles`)}
              </h2>
              {!hasRoleCatalog && (
                <p className="text-xs text-muted-foreground">{t(`${fp}.hints.rolePickerFallback`)}</p>
              )}

              {/* Role mappings sub-group */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-foreground">
                  {t(`${dp}.roles.mappingsLabel`)}
                </h3>
                <p className="text-xs text-muted-foreground">{t(`${fp}.hints.roleMappings`)}</p>
                {draft.roleMappings.length === 0 && (
                  <p className="text-sm text-muted-foreground" role="status">
                    {t(`${fp}.roleMappings.empty`)}
                  </p>
                )}
                {draft.roleMappings.map((mapping, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      aria-label={t(`${fp}.roleMappings.externalAriaLabel`, { index: index + 1 })}
                      value={mapping.externalRole}
                      onChange={(e) => {
                        updateRoleMapping(index, { externalRole: e.target.value });
                      }}
                      placeholder={t(`${fp}.roleMappings.externalPlaceholder`)}
                      autoComplete="off"
                      className="font-mono text-sm"
                    />
                    <span aria-hidden="true" className="text-muted-foreground">
                      →
                    </span>
                    {hasRoleCatalog ? (
                      <Select
                        aria-label={t(`${fp}.roleMappings.internalAriaLabel`, { index: index + 1 })}
                        value={mapping.internalRole}
                        onChange={(e) => {
                          updateRoleMapping(index, { internalRole: e.target.value });
                        }}
                        className="font-mono text-sm"
                      >
                        <option value="">{t(`${fp}.roleMappings.internalPlaceholder`)}</option>
                        {roleOptionsFor(mapping.internalRole).map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Input
                        aria-label={t(`${fp}.roleMappings.internalAriaLabel`, { index: index + 1 })}
                        value={mapping.internalRole}
                        onChange={(e) => {
                          updateRoleMapping(index, { internalRole: e.target.value });
                        }}
                        placeholder={t(`${fp}.roleMappings.internalPlaceholder`)}
                        autoComplete="off"
                        className="font-mono text-sm"
                      />
                    )}
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label={t(`${fp}.roleMappings.removeAriaLabel`, { index: index + 1 })}
                      onClick={() => {
                        setDraft((p) =>
                          p === null
                            ? p
                            : { ...p, roleMappings: p.roleMappings.filter((_, i) => i !== index) },
                        );
                      }}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRoleAddKind('mapping');
                    setRiskyAction('role');
                  }}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {t(`${fp}.roleMappings.add`)}
                </Button>
              </div>

              {/* Default roles sub-group */}
              <div className="space-y-3 border-t border-border pt-5">
                <h3 className="text-sm font-medium text-foreground">
                  {t(`${dp}.roles.defaultsLabel`)}
                </h3>
                <p className="text-xs text-muted-foreground">{t(`${fp}.hints.defaultRoles`)}</p>
                {draft.defaultRoles.length === 0 && (
                  <p className="text-sm text-muted-foreground" role="status">
                    {t(`${fp}.defaultRoles.empty`)}
                  </p>
                )}
                {draft.defaultRoles.map((role, index) => (
                  <div key={index} className="flex items-center gap-2">
                    {hasRoleCatalog ? (
                      <Select
                        aria-label={t(`${fp}.defaultRoles.ariaLabel`, { index: index + 1 })}
                        value={role}
                        onChange={(e) => {
                          updateDefaultRole(index, e.target.value);
                        }}
                        className="font-mono text-sm"
                      >
                        <option value="">{t(`${fp}.defaultRoles.placeholder`)}</option>
                        {roleOptionsFor(role).map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Input
                        aria-label={t(`${fp}.defaultRoles.ariaLabel`, { index: index + 1 })}
                        value={role}
                        onChange={(e) => {
                          updateDefaultRole(index, e.target.value);
                        }}
                        placeholder={t(`${fp}.defaultRoles.placeholder`)}
                        autoComplete="off"
                        className="font-mono text-sm"
                      />
                    )}
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label={t(`${fp}.defaultRoles.removeAriaLabel`, { index: index + 1 })}
                      onClick={() => {
                        setDraft((p) =>
                          p === null
                            ? p
                            : { ...p, defaultRoles: p.defaultRoles.filter((_, i) => i !== index) },
                        );
                      }}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setRoleAddKind('default');
                    setRiskyAction('role');
                  }}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {t(`${fp}.defaultRoles.add`)}
                </Button>
              </div>
            </section>

            {/* PTV2-1524: per-provider token/userinfo capture for login debugging (collapsed by default). */}
            <LoginCaptureSection host={draft.host} />

            {update.isError && (
              <p role="alert" className="text-sm text-red-600">
                {t(`${dp}.saveError`, { code: getErrorCodeForUi(update.error) })}
              </p>
            )}
          </div>
        </div>
      </form>

      <TypeToConfirmDialog
        open={riskyAction !== null}
        title={riskyAction !== null ? t(`${fp}.confirm.${riskyAction}.title`) : ''}
        description={riskyAction !== null ? t(`${fp}.confirm.${riskyAction}.description`) : ''}
        instructions={t(`${fp}.confirm.instructions`, { word: confirmWord })}
        placeholder={t(`${fp}.confirm.placeholder`, { word: confirmWord })}
        confirmWord={confirmWord}
        confirmLabel={riskyAction !== null ? t(`${fp}.confirm.${riskyAction}.confirmLabel`) : ''}
        cancelLabel={t(`${fp}.confirm.cancel`)}
        onCancel={cancelRiskyAction}
        onConfirm={confirmRiskyAction}
      />
    </div>
  );
}
